import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* Futuristic live rate strip — HUD stock-ticker of real selling prices under the hero.
   Data from /api/prices/ticker (live DB). Pauses on hover; reduced-motion safe. */
export default function LiveRatesStrip() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        const pull = () =>
            fetch(`${API}/api/prices/ticker`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => { if (alive && d && d.items && d.items.length) setData(d); })
                .catch(() => {});
        pull();
        const iv = setInterval(pull, 60000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    if (!data) return null;

    const chip = (it, i, key) => (
        <Link
            key={key}
            to={`/prices?sku=${encodeURIComponent(it.sku)}`}
            data-testid={i === 0 ? `rate-chip-${key}` : undefined}
            className="rate-cell inline-flex items-center gap-2.5 mx-1.5 px-3.5 py-1.5 shrink-0 cursor-pointer"
            title={`Open ${it.label} in the estimate builder`}
        >
            <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-dim">{String(((i % data.items.length) + 1)).padStart(2, "0")}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-mist">{it.label}</span>
            <span className="rate-price font-mono text-xs font-semibold text-money tabular-nums">
                ₹{Number(it.price).toLocaleString("en-IN")}
                <span className="text-dim font-normal text-[9px]">/{it.unit === "metre" ? "m" : it.unit}</span>
            </span>
            {it.dir === "up" && <span className="font-mono text-[10px] text-amber-500" title="Price increased at last update" data-testid={`rate-dir-up-${key}`}>▲</span>}
            {it.dir === "down" && <span className="font-mono text-[10px] text-emerald-500" title="Price reduced at last update" data-testid={`rate-dir-down-${key}`}>▼</span>}
        </Link>
    );

    return (
        <section className="rates-hud relative border-y border-line" data-testid="live-rates-strip">
            <span className="rates-hud-scan" aria-hidden="true" />
            <div className="max-w-7xl mx-auto flex items-stretch">
                <Link
                    to="/prices"
                    data-testid="live-rates-label"
                    className="rates-hud-label group relative z-10 flex items-center gap-2.5 bg-copper text-white font-mono text-[10px] uppercase tracking-[0.22em] px-4 md:px-6 py-4 shrink-0 hover:bg-copper-bright transition-colors duration-300"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="live-beacon-ping absolute inline-flex h-full w-full rounded-full bg-white" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    <Activity size={13} />
                    <span className="hidden sm:inline">Live prices</span>
                    <span className="sm:hidden">Live</span>
                    <span className="hidden md:inline text-white/70">· {data.count} rates</span>
                </Link>
                <div className="relative flex-1 overflow-hidden rates-marquee-mask">
                    <div className="rates-marquee flex items-center h-full py-2.5 whitespace-nowrap" data-testid="rates-marquee">
                        {data.items.map((it, i) => chip(it, i, `a-${i}`))}
                        {data.items.map((it, i) => chip(it, i, `b-${i}`))}
                    </div>
                </div>
                <Link
                    to="/prices"
                    data-testid="live-rates-cta"
                    className="rates-hud-cta relative z-10 hidden sm:flex items-center gap-1.5 px-4 md:px-5 font-mono text-[10px] uppercase tracking-[0.18em] text-copper border-l border-line hover:bg-copper hover:text-white transition-colors duration-300 shrink-0"
                >
                    Build estimate <ArrowUpRight size={12} />
                </Link>
            </div>
        </section>
    );
}
